// ============================================================
// THE DISPLAY'S SHOTS - where the camera should end up for a turn (plan
// map-display, Track C). How it gets there is `cameraMotion.ts`.
//
// The rules the user arrived at (2026-10-07, Focus v2 and Session II):
//   - ONE move per turn: a TWO-SHOT that fits the shooter and the target,
//     feet and heads, turning no more than MAX_TURN degrees towards seeing
//     them side-on, then it holds. No push-in, no pull-back, no move to the
//     receiver; a critical is a held beat with no move at all.
//   - OVER THE SHOULDER, a per-viewer option, only when the viewer's OWN
//     character acts: behind and to the right of it, pitch 13, strong
//     perspective, the whole actor in frame at the lower left. A target far
//     above: aim nearer the actor and tilt up, as low as -6.
//   - An actor this viewer may not see (hidden, or veiled on a player's
//     screen) gets no move: the camera must not point at it.
//
// A shot is a `Camera`. Fitting searches the zoom (bisection): under
// perspective the eye moves with the zoom, so there is no closed form, and
// whether everything fits only grows worse as the zoom grows.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from '../battlefield/types';
import { clamp, project, screenRight, type Camera, type Viewport } from '../battlefield/projection';
import { centroid } from '../battlefield/solids';
import { yawDelta } from './timeline';
import { isHidden, type OccludingFace } from './occlusion';

/** One figure as a shot frames it: where it stands, and how tall it is. */
export interface Framed {
  x: number;
  z: number;
  elevation: number;
  height: number;
}

/** The display's "mild perspective": its vertical field of view, in degrees. */
export const DISPLAY_FOV = 30;
/** The furthest one turn may swing the camera, in degrees (the user's ≤25°). */
export const MAX_TURN = 25;
/** Kept clear at each edge of the view, as a share of its size. */
const MARGIN = 0.1;
/** The closest and the furthest a shot will be, in pixels per metre. */
const NEAREST = 90;
const FURTHEST = 3;
const SHOULDER_PITCH = 13;
const LOWEST_PITCH = -6;
const SHOULDER_FOV = 60;
/** Over the shoulder, how far right of the line the held point sits, in metres: the actor goes left. */
const SHOULDER_SIDE = 1.2;
/** A target this much higher than the actor is "far above", in metres. */
const RAISED = 2;
const DEG = Math.PI / 180;

/** Each figure's feet and head: what a shot must keep in frame. */
export function framePoints(figures: readonly Framed[]): Vec3[] {
  return figures.flatMap((f) => [
    { x: f.x, y: f.elevation, z: f.z },
    { x: f.x, y: f.elevation + f.height, z: f.z },
  ]);
}

/** Held at the middle of these points, lifted to their middle height. */
function centredOn(camera: Camera, points: readonly Vec3[]): Camera {
  const mid = centroid(points);
  return { ...camera, target: { x: mid.x, z: mid.z }, lift: mid.y };
}

const within = (n: number, edge: number, size: number) => n >= edge && n <= size - edge;

/** Every point in front of the eye and inside the view, clear of the margin. */
function fits(camera: Camera, points: readonly Vec3[], view: Viewport): boolean {
  return points.every((p) => {
    const s = project(p, camera, view);
    return !s.behind && within(s.x, view.width * MARGIN, view.width) && within(s.y, view.height * MARGIN, view.height);
  });
}

/** The closest zoom at which every point is in frame (FURTHEST when none is). */
function fitZoom(camera: Camera, points: readonly Vec3[], view: Viewport): number {
  const at = (zoom: number) => fits({ ...camera, zoom }, points, view);
  if (at(NEAREST)) return NEAREST;
  if (!at(FURTHEST)) return FURTHEST;
  let lo = FURTHEST;
  let hi = NEAREST;
  for (let i = 0; i < 30; i++) {
    const mid = Math.sqrt(lo * hi);
    if (at(mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** The yaw that looks along (dx, dz) on the ground: the inverse of `viewDirection`. */
const yawLooking = (dx: number, dz: number) => Math.atan2(-dx, -dz) / DEG;

/** Of the two yaws that see a pair side-on, the one nearer `yaw`. */
function sideOn(yaw: number, a: Framed, b: Framed): number {
  const across = yawLooking(-(b.z - a.z), b.x - a.x);
  const other = across + 180;
  return Math.abs(yawDelta(yaw, across)) <= Math.abs(yawDelta(yaw, other)) ? across : other;
}

/**
 * The two-shot from `camera`: turned towards side-on by at most MAX_TURN,
 * held on the middle of the actor, the target and `others`, and as close as
 * keeps all their feet and heads in frame. Pitch and lens are the camera's.
 */
export function twoShot(
  camera: Camera,
  actor: Framed,
  target: Framed | null,
  view: Viewport,
  others: readonly Framed[] = [],
): Camera {
  const turn = target ? clamp(yawDelta(camera.yaw, sideOn(camera.yaw, actor, target)), -MAX_TURN, MAX_TURN) : 0;
  const points = framePoints([actor, ...(target ? [target] : []), ...others]);
  const aimed = centredOn({ ...camera, yaw: camera.yaw + turn }, points);
  return { ...aimed, zoom: fitZoom(aimed, points, view) };
}

/**
 * Over the shoulder of `actor` at `target`: looking along the line between
 * them, held a little right of it (so the actor stands lower left), pitch 13
 * with strong perspective. A target far above pulls the held point back
 * towards the actor and tilts the camera up, never below -6.
 */
export function overShoulder(actor: Framed, target: Framed, view: Viewport): Camera {
  const dx = target.x - actor.x;
  const dz = target.z - actor.z;
  const yaw = yawLooking(dx, dz);
  const rise = target.elevation - actor.elevation;
  const raised = rise > RAISED;
  const aim = raised ? 0.35 : 0.6;
  const right = screenRight(yaw);
  const held = {
    x: actor.x + dx * aim + right.x * SHOULDER_SIDE,
    z: actor.z + dz * aim + right.z * SHOULDER_SIDE,
  };
  const lift = actor.elevation + actor.height * 0.6 + rise * aim;
  const pitch = raised ? clamp(SHOULDER_PITCH - rise * 2, LOWEST_PITCH, SHOULDER_PITCH) : SHOULDER_PITCH;
  const camera: Camera = { yaw, pitch, zoom: FURTHEST, target: held, lift, fov: SHOULDER_FOV };
  return { ...camera, zoom: fitZoom(camera, framePoints([actor, target]), view) };
}

/** The swings tried, in order, when something hides who the shot is about (the Focus demo's). */
const SWINGS = [0, 15, -15, 30, -30];

/** The furthest a move may turn with its swing, from the camera it left (the user's "no more than 30"). */
const MAX_SWUNG_TURN = 30;

/**
 * NEVER LOSE THE ACTOR (the Focus option's swing): the shot, or the first of
 * small swings either way from which none of `points` (the actor's and the
 * target's middles) is hidden; the shot itself when none sees round. `seen`
 * gives the faces that can hide (the opaque ones). A swing never takes the
 * move's whole turn from `fromYaw` past MAX_SWUNG_TURN.
 */
export function swungClear(
  shot: Camera,
  points: readonly Vec3[],
  seen: (camera: Camera) => readonly OccludingFace[],
  view: Viewport,
  fromYaw: number,
): Camera {
  for (const camera of swingsOf(shot, fromYaw)) {
    const faces = seen(camera);
    if (points.every((p) => !isHidden(project(p, camera, view), faces))) return camera;
  }
  return shot;
}

/** The shot and its swings, in order, leaving out any that would turn the whole move past MAX_SWUNG_TURN. */
function swingsOf(shot: Camera, fromYaw: number): Camera[] {
  return SWINGS.filter((swing) => !swing || Math.abs(yawDelta(fromYaw, shot.yaw + swing)) <= MAX_SWUNG_TURN).map(
    (swing) => (swing ? { ...shot, yaw: shot.yaw + swing } : shot),
  );
}
